import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, MapPin } from "lucide-react"; // Added MapPin
import { PopupModal } from "react-calendly";

// Reworked Hero Component
function Hero() {
  const [openBooking, setOpenBooking] = useState(false);

  return (
    <section className="relative bg-gradient-to-b from-gray-50 to-white pt-5 sm:pt-32 sm:pb-24 md:pt-40 md:pb-32 overflow-hidden sm:min-h-0">
      {/* Enhanced Background Image - More visible and aspirational */}
      <div className="relative sm:absolute sm:inset-0">
        <picture className="w-full sm:h-full block">
          <source
            srcSet="/images/springfresh_mobile.webp"
            type="image/webp"
            media="(max-width: 640px)"
          />
          <source
            srcSet="/images/springfresh.webp"
            type="image/webp"
            sizes="(max-width: 1024px) 768px, 1920px"
          />
          <img
            src="/images/springfresh.jpg"
            alt="Hero room"
            fetchpriority="high"
            className="w-full h-auto sm:h-full sm:object-cover sm:object-center"
            width={1920}
            height={1080}
          />
        </picture>
        {/* Mobile-only button - overlaid on image */}
        <div className="sm:hidden absolute bottom-16 left-1/2 -translate-x-1/2 z-10">
          <Link
            to="/shop"
            className="border-2 border-primary inline-block bg-primary text-white px-5 py-2 rounded-lg font-semibold text-sm text-center shadow-md hover:bg-primary/90 transition-all duration-300"
          >
            Shop Now & Save
          </Link>
        </div>
      </div>
      {/* Overlay for text readability */}
      <div className="hidden sm:block absolute inset-0 sm:bg-white/10 md:bg-transparent sm:bg-gradient-to-r sm:from-white/70 sm:via-white/20 sm:to-transparent"></div>

      <div className="hidden sm:block container ml-4 mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex items-center gap-8">
          <div className="max-w-2xl text-center sm:text-left">
            <p className="text-base sm:text-xl md:text-2xl lg:text-3xl font-semibold text-gray-900 mb-1">
              Valid Until March 31, 2026
            </p>
            {/* Headline - Clearer Benefit & Scope */}
            <motion.h1
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight"
            >
              <span className="block text-[#BE2026] text-xs sm:text-sm md:text-xl lg:text-3xl font-sans tracking-widest font-bold">
                SITEWIDE
              </span>
              <span className="font-prata font-normal text-4xl sm:text-5xl md:text-7xl lg:text-8xl">
                Spring
              </span>
              <br />
              <span className="font-bold font-poppins">Refresh Event</span>
              <br /> {/* Force line break for clarity */}
              {/* Prominent Savings */}
            </motion.h1>
            {/* Subheadline - Reinforce Key Selling Points (Customer Obsession: Address quality, origin, ease) */}
            <motion.p
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
              className="mt-4 text-sm sm:text-base md:text-xl lg:text-3xl font-poppins text-gray-700"
            >
              Upgrade your space with our stylish,
              <br /> easy-care fabrics
            </motion.p>

            {/* CTAs - Clear Primary and Secondary Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
              className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4"
            >
              <Link
                to="/shop" // Direct link to product browsing/customization
                className="border-2 border-primary inline-block w-full sm:w-auto bg-primary text-white px-8 py-3 rounded-lg font-semibold text-lg text-center shadow-md hover:bg-primary/90 transition-all duration-300"
              >
                Shop Now & Save
              </Link>
              {/* <Link
              to="/swatches" // Promote free samples early (Reduces customer risk)
              className="inline-block w-full sm:w-auto border-2 border-secondary text-secondary px-8 py-3 rounded-lg font-semibold text-lg text-center hover:bg-secondary/10 transition-all duration-300"
            >
              Get Free Samples
            </Link> */}
              {/* Optional: Consultation Button - Kept less prominent than Shop/Samples initially */}
              {/* <button
                            onClick={() => setOpenBooking(true)}
                            className="inline-block w-full sm:w-auto text-gray-600 hover:text-primary font-medium text-center transition-all duration-300"
                        >
                            Book Free Consultation
                        </button> */}
            </motion.div>
            {/* Trust Badges - More Specific and Trust-Building */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6, ease: "easeOut" }}
              className="mt-8 flex flex-col sm:flex-row gap-4 sm:gap-6 text-sm text-gray-600"
            >
              {/* <div className="flex items-center gap-1.5">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span>Free Shipping Canada-Wide</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
              <span>Handcrafted in Toronto</span>
            </div> */}
              {/* Consider adding a star rating if you have good aggregated reviews */}
              {/* <div className="flex items-center gap-1.5">
                            <Star className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                            <span>4.9/5 Customer Rating</span>
                        </div> */}
            </motion.div>
          </div>

          {/* Promo Circles */}
          <div className="flex flex-col items-center flex-shrink-0">
            {/* Red circle - PLUS EXTRA 30% */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 rounded-full bg-[#BE2026] flex flex-col items-center justify-center gap-0 text-white text-center shadow-lg relative z-10 translate-x-8"
            >
              <span className="font-poppins font-semibold tracking-widest">
                <span className="-mt-3 block text-base sm:text-lg md:text-md leading-none">
                  PLUS
                </span>
                <span className="-mt-3 block text-base sm:text-lg md:text-md leading-none">
                  EXTRA
                </span>
              </span>
              <span className="font-prata text-4xl sm:text-5xl md:text-7xl leading-none font-light -mt-1">
                30%
              </span>
            </motion.div>
            {/* Black circle - 50% OFF */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="w-36 h-36 sm:w-44 sm:h-44 md:w-56 md:h-56 rounded-full bg-black flex flex-col items-center justify-center text-white text-center shadow-lg -mt-8 sm:-mt-10 md:-mt-12"
            >
              <span className="font-prata text-5xl sm:text-6xl md:text-8xl leading-none font-light">
                50%
              </span>
              <span className="text-base sm:text-lg md:text-2xl font-semibold tracking-widest">
                OFF
              </span>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Calendly Popup Modal */}
      <PopupModal
        url="https://calendly.com/offcutshades"
        rootElement={document.getElementById("root")}
        open={openBooking}
        onModalClose={() => setOpenBooking(false)}
        // Use default Calendly styling or pass specific props if needed
      />
    </section>
  );
}

export default Hero;
